import React, { useMemo, useRef, useState } from 'react';
import classes from './GoogleButtonBlock.module.scss';
import { LinkGoogleCallToAction } from '../../../models/graphql';
import { useDidMount } from '../../../hooks/useDidMount';
import { idGenerator } from '../../../helpers/idGenerator';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { CSSTransition } from 'react-transition-group';
import { Select } from '../../../components/forms/Select';
import { FormElementAction } from '../../../models/basic-objects';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type GoogleButtonBlockViewProps = {
  //
  disabled: boolean;
  updateGoogleButtonHandler: FormElementAction;
  googleButtonValue: LinkGoogleCallToAction;
  formInstance: UseFormReturn;
  selectInputName: string;
};

export const mapLinkGoogleButtonToLabel = (value: LinkGoogleCallToAction): string => {
  switch (value) {
    case LinkGoogleCallToAction.NONE:
      return 'None';
    case LinkGoogleCallToAction.BOOK:
      return 'Book';
    case LinkGoogleCallToAction.ORDER_ONLINE:
      return 'Order online';
    case LinkGoogleCallToAction.BUY:
      return 'Buy';
    case LinkGoogleCallToAction.LEARN_MORE:
      return 'Learn more';
    case LinkGoogleCallToAction.SIGN_UP:
      return 'Sign up';
  }
};

const isLinkGoogleCallToAction = (value: string): value is LinkGoogleCallToAction => {
  return value in LinkGoogleCallToAction;
};

const googleButtonItems: SelectItem[] = Object.keys(LinkGoogleCallToAction).flatMap(key => {
  if (!isLinkGoogleCallToAction(key)) return [];
  return {
    label: mapLinkGoogleButtonToLabel(key),
    value: LinkGoogleCallToAction[key]
  };
});

export const GoogleButtonBlockView = (props: GoogleButtonBlockViewProps): JSX.Element => {
  const { disabled, updateGoogleButtonHandler, formInstance, selectInputName, googleButtonValue } = props;
  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const rules: RegisterOptions = {
    required: ValidationMessages.select.required
  };

  useDidMount(() => {
    // we need to setMount to true, since we want to trigger animation (fade) only when the component has been completely mounted.
    setMount(true);
    return () => {
      setMount(false);
    };
  });

  const id = useMemo(() => idGenerator('googleButton'), []);

  return (
    <CSSTransition in={mount} timeout={300} classNames={'fade'} nodeRef={cssRef} unmountOnExit>
      <div className={classes.GoogleButtonBlock} ref={cssRef}>
        <div className={'grid-x align-middle, align-justify'}>
          <div className={'cell shrink'}>
            <div className={'grid-x align-middle'}>
              <div className={classNames(classes.GoogleButtonBlockLabel, 'cell', 'shrink')}>Google Button</div>
              <div className={'cell shrink'}>
                <Tooltip
                  target={`#${id}`}
                  position={'right'}
                  pointer
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  When a link is attached to your Post, you can choose to add a Call To Action (CTA) button below your
                  Post, which takes visitors to the link when clicked. You can choose one of Google&apos;s pre-defined
                  labels for the button like &apos;Book&apos;, &apos;Order online&apos;, or &apos;Learn more&apos;.
                  Choosing &apos;None&apos; will omit the CTA button, leaving the link only visible in the Post text.
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        <div className={'grid-x margin-top-small-16'}>
          <div className={'cell small-12 large-6'}>
            <Select
              action={updateGoogleButtonHandler}
              disabled={disabled}
              items={googleButtonItems}
              name={selectInputName}
              rules={rules}
              size={'large'}
              selectedValue={googleButtonValue}
              useFormInstance={formInstance}
              hideDefaultSelectOption={true}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};
